<template>
    <div class="page__content seasons">
        <div class="columns">
            <div class="column is-8">
                <h2 class="page__headline">{{ seasons? seasons.length : '' }} {{ $t('seasons.headline') }}</h2>
                <p v-if="$route.query.redirect">
                    You need to login first.
                </p>
                <p class="page__copy" v-if="!$route.query.redirect">
                    {{ $t('seasons.copy') }}
                </p>
            </div>
        </div>

        <section class="seasons__section seasons-editor">
            <draggable class="columns is-multiline is-desktop" v-bind="dragOptions" v-model="seasons" @end="onSortedEnd">
                <div class="column is-half-desktop" v-for="(season, index) in seasons" :key="season.id">
                    <season v-model="seasons[index]" @del="openModal(index)"></season>
                </div>
            </draggable>
        </section>

        <section class="seasons__section seasons-buttons">
            <div class="is-clearfix">
                <a class="button is-primary is-pulled-left" @click="addSeason()">{{ $t('seasons.add') }}</a>

                <router-link class="button is-pulled-right" exact :to="{ name: 'campsites', params: {lang: lang} }" @click.native="nextStepClicked()">
                    <span class="icon is-small">
                        <i class="fa fa-arrow-right" aria-hidden="true"></i>
                    </span>
                    <span>{{ $t('seasons.editCampsites') }}</span>
                </router-link>
            </div>
        </section>

        <div class="modal isActive" :class="{ 'is-active': modal.isActive }">
            <div class="modal-background" @click="closeModal()"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ modal.title }}</p>
                    <button class="delete" @click="closeModal()"></button>
                </header>
                <section class="modal-card-body" v-html="modal.content">
                </section>
                <footer class="modal-card-foot is-clearfix">
                    <a class="button" @click="closeModal()">{{ $t('generic.cancel') }}</a>
                    <a class="button is-danger" @click="deleteSeason()">
                            <span class="icon">
                                <i class="fa fa-trash" aria-hidden="true"></i>
                            </span>
                        <span>{{ $t('seasons.delete') }}</span>
                    </a>
                </footer>
            </div>
        </div>

        <section class="seasons-calendar">
            <calendar v-model="calendarDate" :seasons="seasons"></calendar>
        </section>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import fullCalendar from 'vue-fullcalendar';
import moment from 'moment';

import { Api } from '../../../api';
import { AppModel } from '../../../models';
import { EventBus, Tracking } from '../../../utils';
import { Season, Calendar } from '../../components';

export default {
    name: 'seasons',

    components: {
        draggable,
        fullCalendar,
        Season,
        Calendar,
    },

    watch: {
        seasons(newValue) {
            // TODO: check if you can compare values
            // TODO: or only on save & delete
            if(newValue) {
                AppModel.activeCampground.seasons = newValue;
            }
        },
    },

    data() {
        return {
            dragOptions: {
                handle: '.edit-box__dragger',
                ghostClass: 'sortable-ghost',
            },
            events: [],
            modal: {
                isActive: false,
                title: null,
                content: null
            },
            seasons: [],
            calendarDate: new Date(),
        };
    },

    computed: {
        lang() {
            return AppModel.lang;
        },
    },

    beforeRouteEnter(to, from, next) {
        Api.bootstrap().then(function(response) {
            return Promise.all([response, Api.seasons.fetchSeasons()]);
        }).then(function(response) {
            next((vm) => {
                vm.seasons = response[1].body;
                vm.setDefaultMinimumNights();
            });
        });
    },

    beforeDestroy() {
        this.deleteUnsavedSeasons();
    },

    methods: {
        addSeason() {
            let minimumNights = [];
            for(const ctype in AppModel.activeCampground.campsite_types) {
                minimumNights.push({
                    campsite_type: ctype,
                    amount: 1,
                });
            }
            let season = {
                color: '#000000',
                description: null,
                name: null,
                is_lock: false,
                time_periods: [
                    {
                        date_start: moment().format('YYYY-MM-DD'),
                        date_end: moment().format('YYYY-MM-DD')
                    }
                ],
                minimum_nights: minimumNights,
            };
            Tracking.trackEvent('Seasons', 'addSeason', null, null);
            this.seasons.push(season);
        },

        setDefaultMinimumNights() {
            for(let season of this.seasons) {
                for(let ctype in AppModel.campsiteTypes) {
                    let found = false;
                    for(let mn of season.minimum_nights) {
                        if(mn.campsite_type === ctype) {
                            found = true;
                            break;
                        }
                    }
                    if(!found) {
                        season.minimum_nights.push({
                            campsite_type: ctype,
                            amount: 1,
                        });
                    }
                }
            }
        },

        onSortedEnd() {
            let ordering = [];
            this.seasons.forEach(function(element) {
                ordering.push(element.id);
            });
            Api.seasons.orderSeason({ 'order': ordering })
                .then((response) => {
                    if(response.ok) {
                        Tracking.trackEvent('Seasons', 'sort', 'success', null);
                    } else {
                        console.log('order update failed');
                        Tracking.trackEvent('Seasons', 'sort', 'failed', null);
                    }
                }, () => {
                    console.log('order update failed');
                    Tracking.trackEvent('Seasons', 'sort', 'failed', null);
                });
        },

        deleteSeason() {
            Api.seasons.deleteSeason(this.modal.season)
                .then((response) => {
                    this.seasons.splice(this.modal.index, 1);
                    Api.bootstrap(true);
                    EventBus.$emit(EventBus.UPDATE_CHECKS);
                    Tracking.trackEvent('Seasons', 'delete', 'success', null);
                }, (response) => {
                    console.log('delete failed', response);
                    Tracking.trackEvent('Seasons', 'delete', 'failed', null);
                });
            this.closeModal();
        },

        openModal(index) {
            if(!this.seasons[index].id) {
                this.seasons.splice(index, 1);
                Tracking.trackEvent('Seasons', 'discardUnsaved', null, null);
                return;
            }
            // if season is saved season, show warning modal
            this.modal.title = this.$t('generic.warning');
            this.modal.content = this.$t('generic.modal.delete', { name: this.seasons[index].name });
            this.modal.isActive = true;
            this.modal.season = this.seasons[index];
            this.modal.index = index;
        },

        closeModal() {
            this.modal.isActive = false;
        },

        deleteUnsavedSeasons() {
            for(let season in this.seasons) {
                if(!this.seasons[season].id) {
                    this.seasons.splice(season, 1);
                    Tracking.trackEvent('Seasons', 'discardUnsavedOnLeave', null, null);
                }
            }
        },

        nextStepClicked() {
            Tracking.trackEvent('Seasons', 'nextStep', 'campsites', null);
        }
    }
};
</script>

<style type="text/scss">
@import 'seasons.scss';
</style>



// WEBPACK FOOTER //
// src/views/pages/seasons/seasons.vue